import React, {type ReactNode, useEffect, useRef, useState, useSyncExternalStore} from 'react'
import {router} from '@inertiajs/react'
import {User, UserUtils} from '@duesyco/shared'
import EventAssistantPanel from '@/Pages/Events/Assistant/EventAssistantPanel'
import useMediaQuery from '@/Utils/Hooks/useMediaQuery'
import {
    getAssistantShellSnapshot,
    minimizeAssistant,
    subscribeAssistantShell,
} from '@/Layouts/assistantShell'

const desktopQuery = '(min-width: 1024px)'

interface InertiaPage {
    component: string
    props: {
        auth?: {
            user?: User | null
            organization?: {
                name?: string
            } | null
        }
        event?: {
            id?: string
            name?: string
        } | null
        page?: unknown
    }
}

interface PageContext {
    canAsk: boolean
    eventId: string | null
    subject: string
    tab: string
}

const readContext = (page: InertiaPage): PageContext => {
    const user = page.props.auth?.user ?? null
    const canAsk = UserUtils.canUseStaffAssistant(user)

    const organization = page.props.auth?.organization?.name ?? ''

    if (page.component !== 'Events/EventView') {
        return {canAsk, eventId: null, subject: organization, tab: ''}
    }

    const eventId = typeof page.props.event?.id === 'string' ? page.props.event.id : null
    const tab = typeof page.props.page === 'string' ? page.props.page : ''
    const subject = eventId !== null && typeof page.props.event?.name === 'string' ? page.props.event.name : organization

    return {canAsk, eventId, subject, tab}
}

const StaffAssistantHost = ({initialPage}: {initialPage: InertiaPage}): ReactNode => {
    const [context, setContext] = useState(() => readContext(initialPage))
    const shell = useSyncExternalStore(subscribeAssistantShell, getAssistantShellSnapshot, getAssistantShellSnapshot)
    const desktop = useMediaQuery(desktopQuery)
    const shownGenerationRef = useRef<number | null>(null)

    useEffect(() => {
        const stop = router.on('navigate', (event) => {
            setContext(readContext(event.detail.page as InertiaPage))
        })

        return typeof stop === 'function' ? stop : undefined
    }, [])

    useEffect(() => {
        if (shell.open) {
            document.getElementById('event-assistant-prompt')?.focus({preventScroll: true})

            return
        }

        if (document.getElementById('event-assistant-panel')?.contains(document.activeElement)) {
            document.querySelector<HTMLElement>('[data-testid="event-assistant-toggle"]')?.focus({preventScroll: true})
        }
    }, [shell.open])

    if (shell.open) {
        shownGenerationRef.current = shell.generation
    }

    if (!context.canAsk || shownGenerationRef.current !== shell.generation) {
        return null
    }

    const placement = desktop || context.eventId === null ? 'shell' : 'event'

    return (
        <EventAssistantPanel
            key={shell.generation}
            eventId={context.eventId}
            subject={context.subject}
            page={context.tab}
            placement={placement}
            concealed={!shell.open || !shell.shell}
            onClose={minimizeAssistant}
        />
    )
}

export default StaffAssistantHost
